import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase.config';
import { getSettingsDoc } from './appArtifacts';
import { createTrialSubscription } from './trialSubscriptionUtils';

// Generate 6-digit alphanumeric Company ID
export const generateCompanyId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Check if user has company information
export const hasCompanyInfo = (userData) => {
  return !!(userData.companyName || userData.gstNumber || userData.businessName || 
           userData.organization || userData.companyType);
};

// Company types with "Other" option
export const companyTypes = [
  { value: 'proprietorship', label: 'Proprietorship' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'private_limited', label: 'Private Limited' },
  { value: 'public_limited', label: 'Public Limited' },
  { value: 'ngo', label: 'NGO' },
  { value: 'trust', label: 'Trust' },
  { value: 'society', label: 'Society' },
  { value: 'other', label: 'Other' }
];

// Create company if user has company information
export const createCompanyIfNeeded = async (userData, userId, appId) => {
  try {
    // Check if user already has a company ID
    if (userData.companyId) {
      return { companyId: userData.companyId, isNew: false };
    }

    // Check if user has company information
    if (!hasCompanyInfo(userData)) {
      return { companyId: null, isNew: false };
    }

    // Generate unique company ID
    let companyId;
    let isUnique = false;
    let attempts = 0;
    const maxAttempts = 10;

    while (!isUnique && attempts < maxAttempts) {
      companyId = generateCompanyId();
      
      // Check if company ID already exists (use new path)
      const companyRef = doc(db, 'companies', companyId);
      const companyDoc = await getDoc(companyRef);
      
      if (!companyDoc.exists()) {
        isUnique = true;
      }
      attempts++;
    }

    if (!isUnique) {
      throw new Error('Unable to generate unique company ID');
    }

    // Create company document
    const companyData = {
      companyId,
      companyName: userData.companyName || userData.businessName || 'Unnamed Company',
      gstNumber: userData.gstNumber || '',
      companyType: userData.companyType || 'other',
      companyTypeOther: userData.companyTypeOther || '',
      address: userData.address || '',
      phone: userData.phone || '',
      email: userData.email || '',
      website: userData.website || '',
      createdAt: new Date(),
      updatedAt: new Date(),
      admin: {
        userId,
        email: userData.email,
        role: 'owner',
        addedAt: new Date()
      },
      members: [userId],
      status: 'active'
    };

    // Create company in new schema path
    await setDoc(doc(db, 'companies', companyId), {
      owner: {
        userId,
        email: userData.email,
        role: 'owner',
        addedAt: new Date()
      },
      details: companyData,
      plan: 'pro', // Start with PRO trial
      features: ['basic_accounting', 'advanced_features'], // PRO features
      createdAt: new Date(),
      updatedAt: new Date()
    });

    // 🎁 Create 1-month PRO trial subscription
    console.log('🎁 Creating 1-month PRO trial subscription for new company...');
    let trialResult = null; // Declare outside try-catch
    try {
      trialResult = await createTrialSubscription(db, companyId, userId);
      
      if (trialResult.success) {
        console.log(`✅ Trial subscription created: ${trialResult.message}`);
        console.log('📄 Subscription document path:', `companies/${companyId}/subscription_details/subscription`);
      } else {
        console.error('❌ Failed to create trial subscription:', trialResult.error);
        console.error('⚠️  Company created but subscription missing!');
        console.error('📋 User can manually create subscription from the Subscription page');
        // Continue anyway - company is created
      }
    } catch (error) {
      console.error('❌ Exception during trial subscription creation:', error);
      console.error('📋 Stack trace:', error.stack);
      // Continue anyway - company is created
    }

    // Update user Settings document with company ID and role
    // Use setDoc with merge to handle case where document doesn't exist yet
    try {
      const userRef = getSettingsDoc(userId);
      await setDoc(userRef, {
        companyId,
        companyRole: 'owner',
        companyAddedAt: new Date()
      }, { merge: true });
      console.log('✅ User Settings document updated with company ID:', companyId);
    } catch (settingsError) {
      console.warn('⚠️ Error updating user Settings document (non-critical):', settingsError);
      // Continue - Settings document update is not critical for company creation
      // The company is already created and linked in user_companies
    }

    return { 
      companyId, 
      isNew: true, 
      companyData,
      trialInfo: trialResult?.success ? {
        trialEndDate: trialResult.trialEndDate,
        message: trialResult.message
      } : null
    };
  } catch (error) {
    console.error('Error creating company:', error);
    throw error;
  }
};

// Get company information
export const getCompanyInfo = async (companyId, appId) => {
  try {
    // Use new company structure
    const companyRef = doc(db, 'companies', companyId);
    const companyDoc = await getDoc(companyRef);
    
    if (companyDoc.exists()) {
      const data = companyDoc.data();
      const leg = data.details && typeof data.details === 'object' ? data.details : {};

      // Return data in expected format for backward compatibility
      return {
        companyId: data.companyId || companyId,
        companyName:
          data.name ||
          leg.firmName ||
          leg.companyName ||
          leg.businessName ||
          '',
        logoUrl:
          data.branding?.logoUrl ||
          data.logoUrl ||
          leg.logoUrl ||
          leg.logo ||
          '',
        companyType: data.basicInfo?.companyType || data.companyType || leg.companyType || '',
        businessType: data.basicInfo?.businessType || leg.businessType || '',
        contactPerson: data.basicInfo?.contactPerson || leg.contactPerson || '',
        email: data.basicInfo?.email || data.email || leg.email || '',
        phone: data.basicInfo?.phone || data.phone || leg.phone || leg.contactNumber || '',
        gstNumber:
          data.basicInfo?.gstNumber ||
          data.gstNumber ||
          leg.gstNumber ||
          leg.gstin ||
          '',
        gstType:
          data.basicInfo?.gstType ||
          data.gstType ||
          leg.gstinType ||
          leg.gstType ||
          '',
        currency: data.basicInfo?.currency || data.currency || leg.currency || 'INR',
        address: data.details?.address || leg.address || '',
        city: data.details?.city || leg.city || '',
        state: data.details?.state || leg.state || '',
        pincode: data.details?.pincode || leg.pincode || '',
        country: data.details?.country || leg.country || 'India',
        plan: data.plan || 'basic',
        features: data.features || {},
        status: data.status || 'active',
        owner: data.owner || {},
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting company info:', error);
    return null;
  }
};

// Check if user is company admin
export const isCompanyAdmin = (userData) => {
  if (!userData) return false;
  return userData.companyRole === 'owner' || userData.companyRole === 'admin';
};

// Get company type label
export const getCompanyTypeLabel = (type, otherType = '') => {
  if (type === 'other' && otherType) {
    return otherType;
  }
  
  const companyType = companyTypes.find(ct => ct.value === type);
  return companyType ? companyType.label : 'Other';
}; 